import { reportingPrisma as prisma, OrderItemStatus } from "client";
import { buildPaymentSettlementExpressions } from "services/virtualOrderStatus";

type TicketTypesSalesRow = {
  saleTypes?: Array<{
    type?: string;
    sales?: number;
    capacity?: number;
  }>;
  totalPaidTicketsSold?: number;
  totalFreeTicketsSold?: number;
  totalPaidCapacity?: number;
  totalFreeCapacity?: number;
};

function paymentAndSalesStagesAfterOrderUnwind() {
  return [
  {
    $lookup: {
      from: "Order",
      localField: "orderItems.orderId",
      foreignField: "_id",
      as: "orders",
    },
  },
  { $unwind: { path: "$orders" } },
  {
    $lookup: {
      from: "Payment",
      localField: "orders._id",
      foreignField: "orderId",
      as: "payments",
    },
  },
  {
    $addFields: {
      bookablePrice: { $ifNull: ["$price", 0] },
      isConfirmed: {
        $eq: ["$orderItems.status", OrderItemStatus.CONFIRMED],
      },
      isFreeTicket: {
        $cond: {
          if: { $ne: ["$orderItems.total", null] },
          then: { $lte: ["$orderItems.total", 0] },
          else: { $lte: ["$bookablePrice", 0] },
        },
      },
      isPaidTicket: {
        $cond: {
          if: { $ne: ["$orderItems.total", null] },
          then: { $gt: ["$orderItems.total", 0] },
          else: { $gt: ["$bookablePrice", 0] },
        },
      },
      paymentStatuses: {
        $map: {
          input: "$payments",
          as: "p",
          in: "$$p.status",
        },
      },
    },
  },
  {
    $addFields: {
      ...buildPaymentSettlementExpressions("$paymentStatuses"),
    },
  },
  {
    $group: {
      _id: {
        bookableItemId: "$_id",
        orderItemId: "$orderItems._id",
      },
      type: {
        $first: {
          $ifNull: [
            {
              $arrayElemAt: [
                {
                  $map: {
                    input: { $ifNull: ["$translations", []] },
                    as: "trans",
                    in: "$$trans.name",
                  },
                },
                0,
              ],
            },
            "Unknown",
          ],
        },
      },
      bookablePrice: { $first: "$bookablePrice" },
      quantity: { $first: "$quantity" },
      isConfirmed: { $first: "$isConfirmed" },
      isPaidTicket: { $first: "$isPaidTicket" },
      isFreeTicket: { $first: "$isFreeTicket" },
      anyPaid: { $first: "$anyPaid" },
    },
  },
  {
    $group: {
      _id: "$_id.bookableItemId",
      type: { $first: "$type" },
      totalPaidTicketsSold: {
        $sum: {
          $cond: {
            if: {
              $and: [
                "$isConfirmed",
                "$anyPaid",
                { $eq: ["$isPaidTicket", true] },
              ],
            },
            then: 1,
            else: 0,
          },
        },
      },
      totalFreeTicketsSold: {
        $sum: {
          $cond: {
            if: {
              $and: ["$isConfirmed", { $eq: ["$isFreeTicket", true] }],
            },
            then: 1,
            else: 0,
          },
        },
      },
      paidCapacity: {
        $first: {
          $cond: {
            if: { $gt: ["$bookablePrice", 0] },
            then: { $ifNull: ["$quantity", 0] },
            else: 0,
          },
        },
      },
      freeCapacity: {
        $first: {
          $cond: {
            if: { $eq: ["$bookablePrice", 0] },
            then: { $ifNull: ["$quantity", 0] },
            else: 0,
          },
        },
      },
    },
  },
  {
    $addFields: {
      sales: {
        $add: ["$totalPaidTicketsSold", "$totalFreeTicketsSold"],
      },
    },
  },
  {
    $group: {
      _id: null,
      saleTypes: {
        $push: {
          type: "$type",
          sales: "$sales",
          capacity: {
            $add: ["$paidCapacity", "$freeCapacity"],
          },
        },
      },
      totalPaidTicketsSold: { $sum: "$totalPaidTicketsSold" },
      totalFreeTicketsSold: { $sum: "$totalFreeTicketsSold" },
      totalPaidCapacity: { $sum: "$paidCapacity" },
      totalFreeCapacity: { $sum: "$freeCapacity" },
    },
  },
  ];
}

/** Ticket bookable items only — no Entity collection lookup. */
async function aggregateTicketBookableItemSales(eventId: string) {
  const stages = [
    {
      $match: {
        eventId: { $oid: eventId },
      },
    },
    {
      $lookup: {
        from: "Ticket",
        localField: "_id",
        foreignField: "bookableItemId",
        as: "tickets",
      },
    },
    {
      $match: {
        "tickets.0": { $exists: true },
      },
    },
    {
      $lookup: {
        from: "BookableItemTranslations",
        localField: "_id",
        foreignField: "bookableItemId",
        as: "translations",
      },
    },
    {
      $addFields: {
        ticketIds: {
          $map: {
            input: {
              $filter: {
                input: { $ifNull: ["$tickets", []] },
                as: "ticket",
                cond: {
                  $or: [
                    { $eq: ["$$ticket.deletedAt", null] },
                    { $eq: [{ $type: "$$ticket.deletedAt" }, "missing"] },
                  ],
                },
              },
            },
            as: "ticket",
            in: "$$ticket._id",
          },
        },
      },
    },
    {
      $match: {
        "ticketIds.0": { $exists: true },
      },
    },
    // Unwind + equality lookup so OrderItem_ticketId_idx is used.
    // $expr+$in inside $lookup forces COLLSCAN (proven on staging).
    { $unwind: "$ticketIds" },
    {
      $lookup: {
        from: "OrderItem",
        localField: "ticketIds",
        foreignField: "ticketId",
        as: "orderItems",
      },
    },
    { $unwind: { path: "$orderItems" } },
    ...paymentAndSalesStagesAfterOrderUnwind(),
  ];

  const result = await prisma.bookableItem.aggregateRaw({
    pipeline: stages,
  });
  return (Array.isArray(result) ? result : []) as TicketTypesSalesRow[];
}

/**
 * Entity-tier sales — resolve this event's entity ids first (indexed),
 * then only load those OrderItems (avoids scanning all confirmed entity rows).
 */
async function aggregateEntityTierBookableItemSales(eventId: string) {
  const tiers = await prisma.entityTier.findMany({
    where: {
      eventId,
      OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
    },
    select: { id: true },
  });

  if (tiers.length === 0) {
    return [] as TicketTypesSalesRow[];
  }

  const tierIds = tiers.map((tier) => tier.id);
  const entities = await prisma.entity.findMany({
    where: {
      entityTierId: { in: tierIds },
      OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
    },
    select: { id: true },
  });

  if (entities.length === 0) {
    return [] as TicketTypesSalesRow[];
  }

  // ponytail: huge floor plans → large entityId $in. Ceiling ~tens of thousands of
  // seats per event; if explain shows planner pain, chunk $in or denormalize eventId on OrderItem.
  const entityOids = entities.map((entity) => ({ $oid: entity.id }));

  const stages = [
    {
      $match: {
        status: OrderItemStatus.CONFIRMED,
        entityId: { $in: entityOids },
      },
    },
    {
      $lookup: {
        from: "Entity",
        localField: "entityId",
        foreignField: "_id",
        as: "entity",
      },
    },
    { $unwind: { path: "$entity" } },
    {
      $lookup: {
        from: "EntityTier",
        localField: "entity.entityTierId",
        foreignField: "_id",
        as: "tier",
      },
    },
    { $unwind: { path: "$tier" } },
    {
      $lookup: {
        from: "BookableItem",
        localField: "tier.bookableItemId",
        foreignField: "_id",
        as: "bookableItem",
      },
    },
    { $unwind: { path: "$bookableItem" } },
    {
      $lookup: {
        from: "BookableItemTranslations",
        localField: "bookableItem._id",
        foreignField: "bookableItemId",
        as: "translations",
      },
    },
    {
      $replaceRoot: {
        newRoot: {
          _id: "$bookableItem._id",
          price: "$bookableItem.price",
          quantity: "$bookableItem.quantity",
          translations: "$translations",
          orderItems: {
            _id: "$_id",
            orderId: "$orderId",
            status: "$status",
            total: "$total",
          },
        },
      },
    },
    ...paymentAndSalesStagesAfterOrderUnwind(),
  ];

  const result = await prisma.orderItem.aggregateRaw({
    pipeline: stages,
  });
  return (Array.isArray(result) ? result : []) as TicketTypesSalesRow[];
}

function mergeTicketTypesSalesRows(
  ticketRows: TicketTypesSalesRow[],
  entityRows: TicketTypesSalesRow[]
): TicketTypesSalesRow[] {
  const ticket = ticketRows[0];
  const entity = entityRows[0];

  if (!ticket && !entity) return [];
  if (!ticket) return entityRows;
  if (!entity) return ticketRows;

  return [
    {
      saleTypes: [...(ticket.saleTypes ?? []), ...(entity.saleTypes ?? [])],
      totalPaidTicketsSold:
        (ticket.totalPaidTicketsSold ?? 0) + (entity.totalPaidTicketsSold ?? 0),
      totalFreeTicketsSold:
        (ticket.totalFreeTicketsSold ?? 0) +
        (entity.totalFreeTicketsSold ?? 0),
      totalPaidCapacity:
        (ticket.totalPaidCapacity ?? 0) + (entity.totalPaidCapacity ?? 0),
      totalFreeCapacity:
        (ticket.totalFreeCapacity ?? 0) + (entity.totalFreeCapacity ?? 0),
    },
  ];
}

export const getTicketTypesSales = async ({
  eventId,
  filters,
}: {
  eventId: string;
  filters: { [key: string]: string };
}) => {
  try {
    void filters;
    const [ticketRows, entityRows] = await Promise.all([
      aggregateTicketBookableItemSales(eventId),
      aggregateEntityTierBookableItemSales(eventId),
    ]);

    return mergeTicketTypesSalesRows(ticketRows, entityRows);
  } catch (error) {
    console.error(error);
    throw error;
  }
};
